import _ from 'lodash'
import * as THREE from 'three'

import Config from '@glass-designer/config'
import { EditorUserError } from './../utils/editorErrors'
import MathsUtils from '@/utils/mathsUtils'
import EditorUtils from './../utils/editorUtils'
import BaseObject from '../objects/baseObject'
import CircleObject from '../objects/circleObject'
import LineObject from '../library/three/LineObject'
import LabelObject from '../objects/labelObject'
import DashedLineObject from '@glass-designer/objects/dashedLineObject'
import GlassShape from '../model/glassShape'

import GlassDesignerTypes from '../types'


/*
 * GlassSplitLine
 * A floating split line for a shape
 * Used for stock-only panels to mark the horizontal or vertical splitting line
 */
export default class GlassSplitLine extends BaseObject {
  constructor(editor, lineData = {}) {
    super(editor)
    this.objectClassName = 'GlassSplitLine'

    // Rendering details
    this.sceneObject = new THREE.Object3D()
    this.editor.scene.add(this.sceneObject)

    this.dashedLineObject = new DashedLineObject(this.editor, {
      colour: this.editor.getTheme().splitLineColour,
      dashScale: 1.5,
      dashSize: 12,
      gapSize: 8
    })
    this.dashedLineObject.setVisible(true)
    this.addChildObject(this.dashedLineObject)

    this.selectedLine = null
    this.displayIndexCircleObj = null
    this.displayIndexLabel = null

    this.splitIndex = lineData.splitIndex !== undefined ? parseInt(lineData.splitIndex) : 0
    this.parentOffset = new THREE.Vector2(0, 0)
    this.startPosition = new THREE.Vector2(0, 0)
    this.endPosition = new THREE.Vector2(0, 0)

    if(lineData.startPosition)
      this.startPosition.set(lineData.startPosition.x, lineData.startPosition.y)
    if(lineData.endPosition)
      this.endPosition.set(lineData.endPosition.x, lineData.endPosition.y)
    if(lineData.parentOffset)
      this.parentOffset.set(lineData.parentOffset.x, lineData.parentOffset.y)

    this.listenMouseEvents = true

    this.displayIndexCircleObj = new CircleObject(editor, {
      colour: this.editor.getTheme().splitLineDisplayCircleColour,
      opacity: this.editor.getTheme().splitLineDisplayCircleOpacity,
      radius: 25
    })
    this.addChildObject(this.displayIndexCircleObj)

    this.displayIndexLabelObj = new LabelObject(editor, {
      colour: this.editor.getTheme().splitLineDisplayCircleLabelColour,
      fontSize: 36
    })
    this.addChildObject(this.displayIndexLabelObj)

    this.updateSceneObject()
  }


  /**
   * Dispose the object
   */
  dispose() {
    super.dispose()

    // Remove stuff
    if(this.dashedLineObject) {
      this.dashedLineObject.dispose()
      this.dashedLineObject = null
    }
    if(this.selectedLine) {
      this.selectedLine.dispose()
      this.selectedLine = null
    }
    if(this.displayIndexCircleObj) {
      this.displayIndexCircleObj.dispose()
      this.displayIndexCircleObj = null
    }
    if(this.displayIndexLabelObj) {
      this.displayIndexLabelObj.dispose()
      this.displayIndexLabelObj = null
    }
  }


  /**
   * onBeforeRender
   * Called prior to rendering of the object
   */
  onBeforeRender() {
    super.onBeforeRender()

    // Selected support
    if(this.selectedLine) {
      this.selectedLine.updateResolution(this.editor.settings.viewportWidth, this.editor.settings.viewportHeight)
      this.selectedLine.setVisible(this.selected)
    }
  }


  /**
   * Mouse Move Event
   */
  onMouseMoveEvent(ev, mouseData, intersectionData = {}) {
    if(this.editor.isSelectEnabled())
      this.editor.setMouseCursorHand()
  }


  /**
   * Get the length of this splitting line
   */
  getLength() {
    return this.startPosition.distanceTo(this.endPosition)
  }


  /**
   * Calculate the direction of the splitting line
   * Returns a normalized Vector2 object
   */
  getDirection() {
    var dir = new THREE.Vector2()
    dir.subVectors(this.endPosition, this.startPosition)
    dir.normalize()
    return dir
  }


  /**
   * Calculate the outwards direction of the splitting line
   * Returns a normalized Vector2 object
   */
  getOutDirection() {
    let dir = this.getDirection()

    // Rotate by 90deg in 2D
    var outDir = new THREE.Vector2(dir.y, -dir.x)
    return outDir
  }


  /**
   * Calculates the midpoint of the split line
   */
  getMidpoint() {
    return new THREE.Vector2((this.endPosition.x + this.startPosition.x) / 2, (this.endPosition.y + this.startPosition.y) / 2)
  }


  /**
   * Project a point onto the splitting line & return the closest point on the splitting line to the given point
   * Returns a Vector2 object with the closest point
   */
  projectPointOnSplitLine(point, clampEnds = true) {
    return MathsUtils.getClosest2DPointOnLineSegment(this.startPosition, this.endPosition, new THREE.Vector2(point.x, point.y), clampEnds)
  }


  /**
   * Check if the split line is horizontal
   * Split lines should not be anything other than horizontal or vertical
   */
  isHorizontal() {
    return Math.abs(this.endPosition.x - this.startPosition.x) > Math.abs(this.endPosition.y - this.startPosition.y)
  }

  /**
   * Check if the split line is vertical
   * Split lines should not be anything other than horizontal or vertical
   */
  isVertical() {
    return !this.isHorizontal()
  }


  /**
   * Rebuild & recalculate split line details
   * Used when the parent glass shape object has been updated / moved
   */
  rebuildSplitLine() {
    if(this.parentObject && this.parentObject instanceof GlassShape) {
      var boundsMin = this.parentObject.calculateSegmentBounds().min
      var endPointOffset = this.endPosition.clone().sub(this.startPosition)

      this.startPosition.set(boundsMin.x + this.parentOffset.x, boundsMin.y + this.parentOffset.y)
      this.endPosition.set(boundsMin.x + this.parentOffset.x + endPointOffset.x, boundsMin.y + this.parentOffset.y + endPointOffset.y)

      this.startPosition.round()
      this.endPosition.round()
      this.updateSceneObject()
    }
  }


  /**
   * Update the scene object
   */
  updateSceneObject() {
    let labelOffset = 25
    let isHorizontal = Math.abs(this.startPosition.x - this.endPosition.x) > 1

    if(this.selectedLine) {
      this.selectedLine.dispose()
      this.selectedLine = null
    }
    this.clearSceneObject()

    // Display the index/order number of the split index
    if(this.displayIndexCircleObj) {
      if(isHorizontal) {
        let offsetDir = this.startPosition.x < this.endPosition.x ? -1 : 1
        this.displayIndexCircleObj.setPosition(new THREE.Vector2(this.startPosition.x + labelOffset * offsetDir, this.startPosition.y))
      } else {
        let offsetDir = this.startPosition.y < this.endPosition.y ? -1 : 1
        this.displayIndexCircleObj.setPosition(new THREE.Vector2(this.startPosition.x, this.startPosition.y + labelOffset * offsetDir))
      }
    }
    if(this.displayIndexLabelObj) {
      this.displayIndexLabelObj.setText(`${this.splitIndex+1}`)
      this.displayIndexLabelObj.setPosition(this.displayIndexCircleObj.position)
    }

    // Selection line
    this.selectedLine = new LineObject({
      colour: this.editor.getTheme().selectionColour,
      width: this.editor.getTheme().selectionWidth
    })
    this.selectedLine.updateLinePath([this.startPosition, this.endPosition])
    this.sceneObject.add(this.selectedLine.lineObj)

    // Dashed line
    this.dashedLineObject.setStartPosition(this.startPosition)
    this.dashedLineObject.setEndPosition(this.endPosition)
  }


  /**
   * Check for intersections between the object and a ray
   * Returns an object containing intersection point & object position
   * Returns null if no intersection occurred
   */
  checkRayIntersection(ray, worldRadius) {
    // Ignore if not visible
    if(!this.isVisible())
      return null

    var closestIntersection = null
    let radiusTest = Math.max(worldRadius, 0.1)
    let lineRadiusTest = radiusTest * 1.5
    var circleRadiusTest = radiusTest + 25

    // Create a plane from the glass shape
    // Assumes the 2D shape is sitting on the plane at origin (Z = 0)
    var shapePlane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0)
    var rayInt = new THREE.Vector3()
    if(!ray.intersectPlane(shapePlane, rayInt))
      return null

    var ray2DPoint = new THREE.Vector2(rayInt.x, rayInt.y)

    let intPoint = this.projectPointOnSplitLine(ray2DPoint)
    let dist = ray2DPoint.distanceTo(intPoint)
    
    if(dist <= lineRadiusTest) {
      closestIntersection = {
        intersection: intPoint,
        position: ray2DPoint,
        objectId: this.id,
        distance: dist
      }
    } else {
      // Cicle label intersection
      var labelDist = ray2DPoint.distanceTo(this.displayIndexCircleObj.position)
      if(labelDist <= circleRadiusTest) {
        closestIntersection = {
          intersection: this.displayIndexCircleObj.position,
          position: ray2DPoint,
          objectId: this.id,
          distance: labelDist
        }
      }
    }


    return closestIntersection
  }


  /**
   * Calculate the intersection between a line (given a start & end point) and this split line
   * Returns a Vector2 object for the intersection point
   * Returns null if no intersection occured
   */
  calculateLineIntersection(startPoint, endPoint) {
    var intersectionPoint = new THREE.Vector2()
    if(MathsUtils.checkSegmentSegmentIntersection(startPoint, endPoint, this.startPosition, this.endPosition, intersectionPoint)) {
      return intersectionPoint
    }

    return null
  }

  
  /**
   * Clone the GlassSplitLine
   */
  clone() {
    var newSplitLine = new GlassSplitLine(this.editor, {
      splitIndex: this.splitIndex,
      parentOffset: this.parentOffset,
      startPosition: this.startPosition,
      endPosition: this.endPosition
    })

    return newSplitLine
  }


  // Serialize/Deserialize to/from a JSON object
  serialize() {    
    var outputData = super.serialize()

    outputData.splitIndex = this.splitIndex
    outputData.parentOffset = MathsUtils.serializeVector2(this.parentOffset)
    outputData.startPosition = MathsUtils.serializeVector2(this.startPosition)
    outputData.endPosition = MathsUtils.serializeVector2(this.endPosition)

    return outputData
  }
  deserialize(object) {
    super.deserialize(object)

    this.splitIndex = object.splitIndex
    this.parentOffset = MathsUtils.deserializeVector2(object.parentOffset)
    this.startPosition = MathsUtils.deserializeVector2(object.startPosition)
    this.endPosition = MathsUtils.deserializeVector2(object.endPosition)

    this.updateSceneObject()
  }

}